import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Alert, Col, Label, Row, Spinner } from "reactstrap"

import ButtonMain from "components/Common/Button"
import { showMessage } from "components/Notification"
import {
  fetchProviderAvailability,
  updateProviderDefaultTiming,
  updateProviderSlotDuration,
} from "helpers/api_helper"
import AvailabilityPicker from "./AvailabilityPicker"
import AvailabilityDurationInput from "./AvailabilityDurationInput"
import { validateTiming } from "./AvailabilityTimingModal"
import "./Availability.scss"

const DEFAULT_TIMING = {
  startTime: "09:00",
  endTime: "18:00",
  lunchStart: "13:00",
  lunchEnd: "14:00",
}

const getErrorMessage = error =>
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again."

const requireSuccessfulResponse = response => {
  if (!response || response.status !== 1 || !response.data) {
    throw new Error(
      response?.message || "The server could not save this change."
    )
  }
  return response
}

const DefaultAvailabilityForm = ({ vetId }) => {
  const [availability, setAvailability] = useState(null)
  const [timing, setTiming] = useState(DEFAULT_TIMING)
  const [slotDuration, setSlotDuration] = useState(30)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [touched, setTouched] = useState(false)
  const [error, setError] = useState("")

  const timingErrors = useMemo(() => validateTiming(timing, false), [timing])
  const durationError = useMemo(() => {
    const duration = Number(slotDuration)
    return !Number.isInteger(duration) || duration <= 0 || duration > 1440
      ? "Enter a positive whole number of minutes (maximum 1440)."
      : ""
  }, [slotDuration])

  const applyAvailability = data => {
    setAvailability(data)
    setTiming({ ...DEFAULT_TIMING, ...(data?.defaultTiming || {}) })
    setSlotDuration(data?.slotDuration || 30)
  }

  useEffect(() => {
    const load = async () => {
      if (!vetId) {
        setError("Your Vet profile could not be identified.")
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        setError("")
        const response = requireSuccessfulResponse(
          await fetchProviderAvailability("vet", vetId)
        )
        applyAvailability(response.data)
      } catch (requestError) {
        setError(getErrorMessage(requestError))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [vetId])

  const updateTiming = event => {
    const { name, value } = event.target
    setTiming(current => ({ ...current, [name]: value }))
    setTouched(true)
    setError("")
  }

  const save = async () => {
    setTouched(true)
    setError("")
    if (Object.keys(timingErrors).length || durationError) return

    try {
      setSaving(true)
      let latest = availability
      const timingChanged = Object.keys(DEFAULT_TIMING).some(
        key => timing[key] !== availability?.defaultTiming?.[key]
      )
      const durationChanged =
        Number(slotDuration) !== availability?.slotDuration

      if (timingChanged) {
        latest = requireSuccessfulResponse(
          await updateProviderDefaultTiming("vet", vetId, timing)
        ).data
        setAvailability(latest)
        setTiming({ ...DEFAULT_TIMING, ...(latest?.defaultTiming || {}) })
      }
      if (durationChanged) {
        latest = requireSuccessfulResponse(
          await updateProviderSlotDuration("vet", vetId, Number(slotDuration))
        ).data
      }
      if (!timingChanged && !durationChanged) {
        showMessage({
          label: "Your default availability is already up to date.",
          status: "success",
        })
        return
      }

      applyAvailability(latest)
      setTouched(false)
      showMessage({
        label: "Default availability saved successfully.",
        status: "success",
      })
    } catch (requestError) {
      const message = getErrorMessage(requestError)
      setError(message)
      showMessage({ label: message, status: "error" })
    } finally {
      setSaving(false)
    }
  }

  const renderTime = (id, name, label) => {
    const fieldError = timingErrors[name]
    const rangeError = name.startsWith("lunch") ? timingErrors.lunchRange : ""
    return (
      <Col sm={6} className="mb-3">
        <Label for={id}>{label}</Label>
        <AvailabilityPicker
          id={id}
          name={name}
          type="time"
          value={timing[name]}
          onChange={updateTiming}
          invalid={touched && !!(fieldError || rangeError)}
        />
        {touched && (fieldError || rangeError) && (
          <div className="invalid-feedback d-block">
            {fieldError || rangeError}
          </div>
        )}
      </Col>
    )
  }

  return (
    <section
      className="availability-profile-section"
      onKeyDown={event => event.key === "Enter" && event.preventDefault()}
    >
      <div className="mb-3">
        <h5 className="mb-1">Default Availability</h5>
        <p className="text-muted mb-0">
          These hours are used for appointments unless a particular-day timing
          or holiday overrides them.
        </p>
      </div>

      {loading ? (
        <div className="availability-inline-loading">
          <Spinner color="primary" size="sm" /> Loading default availability…
        </div>
      ) : (
        <>
          {error && <Alert color="danger">{error}</Alert>}
          <Row>
            <Col lg={6}>
              <h6>Working Hours</h6>
              <Row>
                {renderTime("profile-working-start", "startTime", "Start Time")}
                {renderTime("profile-working-end", "endTime", "End Time")}
              </Row>
            </Col>
            <Col lg={6}>
              <h6>Lunch / Break</h6>
              <Row>
                {renderTime("profile-lunch-start", "lunchStart", "Start Time")}
                {renderTime("profile-lunch-end", "lunchEnd", "End Time")}
              </Row>
            </Col>
            <Col lg={6} className="mb-3">
              <Label for="profile-slot-duration">Appointment Duration</Label>
              <AvailabilityDurationInput
                id="profile-slot-duration"
                value={slotDuration}
                invalid={touched && !!durationError}
                error={durationError}
                onChange={value => {
                  setSlotDuration(value)
                  setTouched(true)
                  setError("")
                }}
              />
            </Col>
          </Row>
          <div className="d-flex justify-content-end">
            <ButtonMain
              type="button"
              className="btn btn-secondary"
              onClick={save}
              isLoading={saving}
              disabled={
                !vetId ||
                saving ||
                !!durationError ||
                !!Object.keys(timingErrors).length
              }
            >
              Save Availability
            </ButtonMain>
          </div>
        </>
      )}
    </section>
  )
}

DefaultAvailabilityForm.propTypes = {
  vetId: PropTypes.string,
}

export default DefaultAvailabilityForm
